import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { BlueColorStart500 } from "../../../constants/constants";
import { USER_COUNTRY } from "../../../constants/constants";
import { CountryCode } from "../../../constants/countryCurrency";
import { useSelector } from "react-redux";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";
am5.addLicense("AM5C258896422");

// Helper function to truncate label
function truncateLabel(label, maxLength = 6) {
    if (typeof label !== "string") return label;
    return label.length > maxLength ? label.slice(0, maxLength) + "..." : label;
}

// Helper to get display label from options
function getLabelFromOptions(options, value) {
    const found = options?.find(opt => opt.value === value);
    return found ? found.label : value.replace(/_/g, " ");
}

// Helper for biggest negative
function findBiggestNegative(array) {
    let biggestNegative = 0;
    for (const obj of array) {
        for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'number' && value < 0) {
                if (value < biggestNegative) {
                    biggestNegative = value;
                }
            }
        }
    }
    return biggestNegative;
}

const CustomBarChart = ({
    chartId,
    chartClass,
    showLegend = true,
    showLabels,
    showVerticalLabels,
    showHalfVerticalLabels,
    chartData,
    chartLabels,
    showShortVerticalLabels,
    chartColorSet,
    minimumGridDistance,
    flatLegends,
    disabledLegendArray,
    setDisabledValues,
    tooltipOptions = [],
    valueOptions = []
}) => {
    // USER DATA
    const userData = useSelector((state) => state.userData.user[0]);
    let userCountryCode = userData?.country_code ?? "IN"

    let country = {}

    CountryCode.map((data) => {
        if (data.countryCode === userCountryCode) {
            country = data
        }
    })

    useLayoutEffect(() => {
        var root = am5.Root.new(chartId);

        // TO CHANGE COLOR OF THE CHART
        const colorSet = chartColorSet ?? BlueColorStart500;

        // Set themes
        root.setThemes([am5themes_Animated.new(root)]);
        root.interfaceColors.set("grid", am5.color(0xffffff)); // to change the color of grid lines

        // Create chart
        var chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                layout: root.verticalLayout,
                paddingRight: 0,
                paddingTop: 5,
                paddingBottom: 0,
                paddingLeft: 0,
                maxTooltipDistance: -1,
            })
        );

        // Add cursor
        var cursor = chart.set(
            "cursor",
            am5xy.XYCursor.new(root, {
                behavior: "zoomXY",
            })
        );
        cursor.lineY.set("visible", false);
        cursor.lineX.set("visible", false);

        // The data
        // displayData already includes label and displayLabel
        // const displayData = (chartData || []);
        // ...existing code...

        // The data
        const displayData = (chartData || []).map(row => ({
            ...row,
            displayLabel: truncateLabel(row.label, 12) // <-- Truncate after 12 chars
        }));

        // ...existing code...

        // X Axis Renderer
        let xRenderer = am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
            cellStartLocation: 0.4,
            cellEndLocation: 0.6,
            minGridDistance: minimumGridDistance || 80,
            stroke: am5.color(0x707070), // axis line color
            strokeWidth: 1,
            strokeOpacity: 1,
        });

        // Always rotate labels (e.g. -45deg)
        xRenderer.labels.template.setAll({
            //         // rotation: -45,
            //         centerY: am5.p50,
            //         centerX: am5.p100,
            //         paddingRight: 15,
            //         oversizedBehavior: "wrap",
            //         maxWidth: 120,
            //         fontSize: 12,
            //         fontFamily: "Ubuntu",
            //         // fill: "#757575",
            // fill: am5.color(0x757575), // label color (same as Waterfall)

            //         fontWeight: "400"
            rotation: 0,
            centerY: am5.p100,
            centerX: am5.p50,
            paddingRight: 0,
            paddingLeft: 0,
            paddingTop: 5,
            paddingBottom: 10,
            oversizedBehavior: "wrap",
            maxWidth: 120,
            fontSize: 12,
            fontFamily: "Ubuntu",
            fill: am5.color(0x757575),
            fontWeight: "400",
            textAlign: "center",
            dy: 10 //
        });

        // Use 'label' as the category field (unique), show displayLabel as tick text
        var xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: "label",
                startLocation: 0.2,
                endLocation: 0.8,
                renderer: xRenderer,
            })
        );
        xAxis.data.setAll(displayData);

        // Show the truncated label in the axis tick text
        xRenderer.labels.template.adapters.add("text", function (text, target) {
            const dataItem = target.dataItem?.dataContext;
            return dataItem?.displayLabel ?? text;
        });

        // Y Axis Renderer (ensure visible axis line)
        var yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                treatZeroAs: 0.000001,
                extraMax: 0.1,
                maxPrecision: 0,
                renderer: am5xy.AxisRendererY.new(root, {
                    stroke: am5.color(0x707070),    // axis line color
                    strokeWidth: 1,                 // make it visible
                    strokeOpacity: 1,               // fully visible
                }),
                min: findBiggestNegative(displayData) + (findBiggestNegative(displayData) * 0.1),
                extraMax: 0.04,
                strictMinMaxSelection: true,
            })
        );

        yAxis.get("renderer").setAll({
            stroke: am5.color(0x707070),
            strokeWidth: 1,
            strokeOpacity: 1,
            visible: true,
        });

        if (showLabels === false) {
            yAxis.get("renderer").labels.template.setAll({
                visible: false,
            });
        } else {
            yAxis.get("renderer").labels.template.setAll({
                oversizedBehavior: "truncate",
                maxWidth: 150,
                textAlign: "center",
                fontSize: 12,
                fontFamily: "Ubuntu",
                // fill: "#757575",
                fill: am5.color(0x757575),
                fontWeight: "400",
            });
        }

        // to format numbers
        root.numberFormatter.setAll({
            numberFormat: "#.##a",
            bigNumberPrefixes: [
                { number: 1e3, suffix: "K" },
                { number: 1e6, suffix: "M" },
                { number: 1e9, suffix: "B" },
            ],
            smallNumberPrefixes: [],
        });

        // Add legend
        var legend = chart.children.push(
            am5.Legend.new(root, {
                position: "relative",
                x: am5.percent(50),
                centerX: am5.percent(50),
                height: chartLabels.length >= 6 ? am5.percent(30) : null,
                marginTop: 0,
                useDefaultMarker: true,
                layout: am5.GridLayout.new(root, {
                    ...(disabledLegendArray === undefined || disabledLegendArray === null ? {
                        maxColumns: 10,
                        fixedWidthGrid: true,
                    } : {
                        maxColumns: 15,
                    }),
                }),
                verticalScrollbar: am5.Scrollbar.new(root, {
                    ...(disabledLegendArray === undefined || disabledLegendArray === null ? {
                        orientation: "vertical",
                    } : {}),
                }),
            })
        );

        if (disabledLegendArray === undefined || disabledLegendArray === null) {
            if (flatLegends === true) {
                legend.markers.template.setAll({
                    width: 12,
                    height: 12,
                    strokeWidth: 2,
                    fillOpacity: 1,
                    strokeOpacity: 1,
                    centerY: am5.p50,
                    centerX: am5.p50,
                });
                legend.markerRectangles.template.setAll({
                    cornerRadiusTL: 2,  // << Rounded top-left
                    cornerRadiusTR: 2,  // << Rounded top-right
                    cornerRadiusBL: 2,  // << Rounded bottom-left
                    cornerRadiusBR: 2, // << Rounded bottom-right
                });
            }
            else {
                legend.markers.template.setAll({
                    width: 12,
                    height: 12,
                    strokeWidth: 2,
                    fillOpacity: 1,
                    strokeOpacity: 1,
                    centerY: am5.p50,
                    centerX: am5.p50,
                });
                legend.markerRectangles.template.setAll({
                    cornerRadiusTL: 2,  // << Rounded top-left
                    cornerRadiusTR: 2,  // << Rounded top-right
                    cornerRadiusBL: 2,  // << Rounded bottom-left
                    cornerRadiusBR: 2,  // << Rounded bottom-right
                });
            }
        }

        if (disabledLegendArray) {
            legend.markers.template.setup = function (marker) {
                var check = am5.Graphics.new(root, {
                    width: 10,
                    height: 10,
                    layer: 50,
                    fill: am5.color(0xffffff),
                    draw: function (display) {
                        display.drawRect(4, 4, 6, 6)
                    }
                });

                check.states.create("disabled", {
                    fillOpacity: 1
                });

                marker.children.push(check);
            }

            legend.markers.template.setAll({
                width: 14,
                height: 14,
            });
            legend.markerRectangles.template.setAll({
                cornerRadiusTL: 0,
                cornerRadiusTR: 0,
                cornerRadiusBL: 0,
                cornerRadiusBR: 0,
            });

            legend.itemContainers.template.events.on('click', function (ev) {
                var temp = ev.target.dataItem.dataContext;

                chart.series.values.filter((i) => {
                    if (temp.get('name') === i._settings.name) {
                        if (i.get('visible')) {
                            setDisabledValues(prev => {
                                var tempArr = [...prev, temp.get('name')]
                                var fooBar = [...new Set(tempArr)]
                                return fooBar;
                            })
                        } else {
                            setDisabledValues(prev => {
                                var tempArrRmv = prev.filter((i) => i !== temp.get('name'))
                                return tempArrRmv
                            })
                        }
                    }
                })
            })
        }

        legend.labels.template.setAll({
            // fontSize: 12,
            // fontWeight: "400",
            // fill: "#213345",
            // oversizedBehavior: "wrap",
            fontFamily: "Ubuntu, sans-serif",
            fontSize: 12,
            fill: am5.color(0x213345), // navy blue
            fontWeight: "400",
            oversizedBehavior: "wrap",
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 5,
            paddingRight: 5,
            textAlign: "center",
        });

        // Add series
        function createSeries(name, field, childData, parents, index) {
            var series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: name,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: field,
                    categoryXField: "label",  // use the unique label
                    childData: childData,
                    parents: parents,
                    fill: am5.color(colorSet[index % colorSet.length]),
                    stroke: am5.color(colorSet[index % colorSet.length]),
                })
            );

            series.columns.template.setAll({
                cornerRadiusTL: 7,
                cornerRadiusTR: 7,
                width: 10,
            });

            var tooltip = series.set(
                "tooltip",
                am5.Tooltip.new(root, {
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    autoTextColor: false,
                    pointerOrientation: "horizontal",
                    position: "pointer",
                    className: "custom-scroll-tooltip"
                })
            );

            tooltip.get("background").setAll({
                fill: am5.color(0x213345),
                oversizedBehavior: "truncate",
                cornerRadius: 5,
                maxHeight: 400,
                className: "custom-scroll-tooltip"
            });

            // Add scroll style dynamically (once)
            if (!document.getElementById("custom-scroll-tooltip-style")) {
                const style = document.createElement('style');
                style.id = "custom-scroll-tooltip-style";
                style.innerHTML = `
                    .custom-scroll-tooltip {
                        max-height: 200px !important;
                        overflow-y: auto !important;
                        overflow-x: hidden !important;
                        scrollbar-color: #bdbdbd #213345;
                        scrollbar-width: thin;
                    }
                    .custom-scroll-tooltip::-webkit-scrollbar {
                        width: 8px;
                        background: #213345;
                    }
                    .custom-scroll-tooltip::-webkit-scrollbar-thumb {
                        background: #bdbdbd;
                        border-radius: 5px;
                    }
                `;
                document.head.appendChild(style);
            }

            tooltip.label.setAll({
                text: "[bold]{label}[/]",
                fill: am5.color(0xffffff),
                fontSize: 12,
                maxWidth: 300,
                oversizedBehavior: "wrap",
                fontFamily: "Ubuntu",

            });

            tooltip.label.adapters.add("text", function (text, target) {
                const firstSeries = chart.series.getIndex(0);
                const tdi = firstSeries.get("tooltipDataItem");
                let groupLabel = tdi?.dataContext?.label || "";
                groupLabel = "[bold]" + groupLabel.toUpperCase() + "[/bold]\n\n";

                let output = groupLabel;

                const parents = firstSeries.get("parents");
                let heading1 = "";
                let heading2 = "";
                if (Array.isArray(parents)) {
                    heading1 = parents[0] || "";
                    heading2 = parents[1] || "";
                }

                function toTitleCase(str) {
                    return str.replace(
                        /\w\S*/g,
                        (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                    );
                }

                // Second heading (e.g. Sales Unit) - ONLY ONCE!
                if (heading2) {
                    output += toTitleCase(getLabelFromOptions(tooltipOptions.concat(valueOptions), heading2)) + "\n";
                    // Find first label with matching parent and render its childData only once
                    const targetLabel = chartLabels.find(
                        label => label.parents && label.parents[1] === heading2 && Array.isArray(label.childData)
                    );
                    if (targetLabel && Array.isArray(targetLabel.childData)) {
                        targetLabel.childData.forEach(child => {
                            output += "● " + child.label + " : " +
                                formatNumberForTooltip(tdi?.dataContext[child.value], child.label) + "\n";
                        });
                    }
                    output += "\n";
                }

                // First heading (e.g. Sales Volume)
                if (heading1) {
                    output += toTitleCase(getLabelFromOptions(tooltipOptions.concat(valueOptions), heading1)) + "\n";
                    // Only include unique label values for this parent
                    const seen = new Set();
                    chartLabels.forEach((label) => {
                        if (
                            label.parents &&
                            label.parents[0] === heading1 &&
                            tdi?.dataContext[label.value] !== undefined &&
                            !seen.has(label.value)
                        ) {
                            output += "● " + label.label + " : " +
                                formatNumberForTooltip(tdi?.dataContext[label.value], label.label) + "\n";
                            seen.add(label.value);
                        }
                    });
                    output += "\n";
                }



                return output;
            });

            series.data.setAll(displayData);

            series.appear(1000);

            if (showLegend === true) {
                legend.data.push(series);
            }
        }

        chartLabels.map((label, index) => {
            createSeries(
                label.label,
                label.value,
                label.childData,
                label.parents,
                index
            );
        });

        // Toggle disabled legend items
        if (disabledLegendArray !== undefined && disabledLegendArray !== null && disabledLegendArray.length !== 0) {
            function toggle(item) {
                if (item === undefined || item === null) return
                var storedItem = item;
                chart.series.values.filter((i, index) => {
                    if (storedItem.includes(i._settings.name)) {
                        var series = chart.series.getIndex(index);
                        toggleSeries(series);
                    }
                })
            }

            function toggleSeries(series) {
                if (series.get("visible")) {
                    series.hide();
                }
                else {
                    series.show();
                }
            }

            setTimeout(() => {
                toggle(disabledLegendArray);
            }, 1)
        }

        // Make stuff animate on load
        chart.zoomOutButton.set("forceHidden", true);
        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [chartData, chartLabels, disabledLegendArray, tooltipOptions, valueOptions]);

    return (
        <Wrapper>
            <div id={chartId} className={chartClass} style={{
                height: "340px",
                minHeight: "340px",
                background: "#fff",
                borderRadius: "12px"
            }}></div>
        </Wrapper>
    );
};

CustomBarChart.propTypes = {
    chartId: PropTypes.string,
    chartClass: PropTypes.string,
    showLegend: PropTypes.bool,
    showLabels: PropTypes.bool,
    showVerticalLabels: PropTypes.bool,
    showHalfVerticalLabels: PropTypes.bool,
    chartData: PropTypes.array,
    chartLabels: PropTypes.array,
    showShortVerticalLabels: PropTypes.bool,
    chartColorSet: PropTypes.array,
    minimumGridDistance: PropTypes.any,
    percentageTooltip: PropTypes.bool,
    flatLegends: PropTypes.bool,
    disabledLegendArray: PropTypes.array,
    setDisabledValues: PropTypes.func,
    tooltipOptions: PropTypes.array,
    valueOptions: PropTypes.array
};

export default CustomBarChart;